import React, { useState, useEffect } from 'react';
import { WhatsAppTemplate, whatsappTemplateService } from '../services/whatsappTemplateService';
import Button from './Button';
import Spinner from './Spinner';

interface WhatsAppTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (templateName: string, languageCode: string, components: any[]) => void;
  phoneNumberId: string;
}

const WhatsAppTemplateModal: React.FC<WhatsAppTemplateModalProps> = ({
  isOpen,
  onClose,
  onSend,
  phoneNumberId,
}) => {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [headerVars, setHeaderVars] = useState<string[]>([]);
  const [bodyVars, setBodyVars] = useState<string[]>([]);
  const [step, setStep] = useState<'select' | 'fill'>('select');

  useEffect(() => {
    if (isOpen) loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await whatsappTemplateService.getTemplates(phoneNumberId);
      setTemplates(data);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (template: WhatsAppTemplate) => {
    setSelectedTemplate(template);
    const vars = whatsappTemplateService.extractVariables(template);
    setHeaderVars(Array(vars.header).fill(''));
    setBodyVars(Array(vars.body).fill(''));
    if (vars.header === 0 && vars.body === 0) {
      handleSend(template, [], []);
    } else {
      setStep('fill');
    }
  };

  const handleSend = (template: WhatsAppTemplate, header: string[], body: string[]) => {
    const components = whatsappTemplateService.buildComponents(template, header, body);
    onSend(template.name, template.language, components);
    handleClose();
  };

  const handleClose = () => {
    setSelectedTemplate(null);
    setHeaderVars([]);
    setBodyVars([]);
    setStep('select');
    onClose();
  };

  const getTemplatePreview = (template: WhatsAppTemplate) => {
    const bodyComponent = template.components.find(c => c.type === 'BODY');
    return bodyComponent?.text || 'Sin vista previa';
  };

  const canSubmit = () =>
    headerVars.every(v => v.trim() !== '') && bodyVars.every(v => v.trim() !== '');

  if (!isOpen) return null;

  const inputCls = 'w-full px-4 py-2.5 bg-brand-light border-0 rounded-xl text-sm text-brand-dark focus:outline-none focus:ring-2 focus:ring-brand-yellow placeholder-brand-mid';
  const labelCls = 'block text-xs font-semibold text-brand-dark mb-1.5';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-brand-surface rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col border border-brand-border">

        {/* Header */}
        <div className="px-6 py-5 flex items-start justify-between flex-shrink-0" style={{ backgroundColor: '#3B4155' }}>
          <div>
            <h2 className="text-lg font-bold text-white">
              {step === 'select' ? 'Seleccionar Plantilla' : 'Completar Variables'}
            </h2>
            <p className="text-white/60 text-sm mt-0.5">
              {step === 'select'
                ? 'Elige una plantilla aprobada para enviar'
                : 'Completa las variables de la plantilla'}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 hover:bg-white/20 rounded-lg transition ml-4 flex-shrink-0 text-white"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner size="lg" />
            </div>
          ) : step === 'select' ? (
            <div className="space-y-3">
              {templates.length === 0 ? (
                <div className="text-center py-14 text-brand-mid">
                  <svg className="w-14 h-14 mx-auto mb-4 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="font-semibold text-brand-dark text-sm">No hay plantillas disponibles</p>
                  <p className="text-xs mt-1">Crea plantillas en el administrador de WhatsApp Business</p>
                </div>
              ) : (
                templates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => handleSelectTemplate(template)}
                    className="w-full text-left p-4 border border-brand-border rounded-xl hover:border-brand-yellow hover:bg-brand-yellow/5 transition bg-brand-surface"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-brand-dark text-sm">{template.name}</h3>
                        <p className="text-xs text-brand-mid mt-1 line-clamp-2">
                          {getTemplatePreview(template)}
                        </p>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <span className="text-[10px] bg-brand-mid/20 text-brand-dark px-2 py-0.5 rounded-full font-medium">
                            {template.language}
                          </span>
                          <span className="text-[10px] bg-brand-yellow/20 text-brand-dark px-2 py-0.5 rounded-full font-medium">
                            {template.category}
                          </span>
                        </div>
                      </div>
                      <svg className="w-4 h-4 text-brand-mid flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </button>
                ))
              )}
            </div>
          ) : (
            selectedTemplate && (
              <div className="space-y-5">
                {/* Vista previa */}
                <div className="bg-brand-light rounded-xl p-4 border border-brand-border">
                  <p className="text-xs font-bold text-brand-dark mb-2">Vista previa</p>
                  {selectedTemplate.components.map((component, idx) => (
                    <div key={idx}>
                      {component.type === 'HEADER' && component.text && (
                        <p className="font-bold text-brand-dark text-sm">{component.text}</p>
                      )}
                      {component.type === 'BODY' && component.text && (
                        <p className="text-brand-dark text-sm whitespace-pre-wrap mt-1">{component.text}</p>
                      )}
                      {component.type === 'FOOTER' && component.text && (
                        <p className="text-xs text-brand-mid mt-2">{component.text}</p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Variables header */}
                {headerVars.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-brand-dark mb-3">Variables del encabezado</p>
                    <div className="space-y-3">
                      {headerVars.map((value, idx) => (
                        <div key={idx}>
                          <label className={labelCls}>Variable {idx + 1}</label>
                          <input
                            type="text"
                            value={value}
                            onChange={(e) => {
                              const n = [...headerVars]; n[idx] = e.target.value; setHeaderVars(n);
                            }}
                            className={inputCls}
                            placeholder={`Valor para {{${idx + 1}}}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Variables body */}
                {bodyVars.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-brand-dark mb-3">Variables del mensaje</p>
                    <div className="space-y-3">
                      {bodyVars.map((value, idx) => (
                        <div key={idx}>
                          <label className={labelCls}>Variable {idx + 1}</label>
                          <input
                            type="text"
                            value={value}
                            onChange={(e) => {
                              const n = [...bodyVars]; n[idx] = e.target.value; setBodyVars(n);
                            }}
                            className={inputCls}
                            placeholder={`Valor para {{${idx + 1}}}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          )}
        </div>

        {/* Footer */}
        {step === 'fill' && selectedTemplate && (
          <div className="border-t border-brand-border px-6 py-4 flex justify-between bg-brand-surface">
            <Button variant="secondary" onClick={() => setStep('select')}>
              ← Volver
            </Button>
            <Button variant="primary" onClick={() => handleSend(selectedTemplate, headerVars, bodyVars)} disabled={!canSubmit()}>
              Enviar Plantilla
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppTemplateModal;
